import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Button, Card, Collapse, Form, InputGroup, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import PaginatedSelectTable from "./FormItems/paginated-select-table";
import { TabActiveContext } from "./FormItems/TabActiveContext";
import { DataSource, QueryFilter, Repository, LogicalConnector, LogicalOperator, SortDirection, createReduxStore } from "./data-source";
import PageHeader from '../../UI/molecules/PageHeader/page-header';
import TableSearchFilter from '../../UI/atoms/TableSearchFilter/table-search-filter';
import { FormItem, FormView } from "./form-view";
import { ModalView } from "./modal-view";
import { BsPencilFill, BsXLg, BsPlusLg } from 'react-icons/bs';
import toast from 'react-hot-toast';
import LoadingContent from '../../UI/molecules/LoadingContent/loading-content';
import { NavLink } from 'react-router-dom';
import RowSelectTable from "./FormItems/Table/row-select-table";
import { SelectFilter } from "../atoms/Filters/SelectFilter";
import { MultiSelectFilter } from "../atoms/Filters/MultiSelectFilter";
import debounce from 'lodash.debounce';
import useEffectDelta from '../../hooks/useEffectDelta';
import { selectCurentUserRoles, selectIsAdmin, selectLoggedInUserPreferences } from "../../../store/slices/auth-slice";
import { setSharedFilter, initializeUrlFilters } from "../../../store/slices/shared-filters-slice";
import { useListUrlOptions } from './use-list-url-options';
import { exportData, mergeData } from "../../../services/table-helpers";
import { formatDate, setDateAsUTC } from "../../../services/format";
import { guid } from "../../../services/form-helpers";
import { reduxReducer, setReduxReducer } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { GreenIcon, RedIcon, YelloIcon } from "../atoms/CircleFillIcon/circle-fill-icon";
import { FormModal } from "./FormItems/FormModal";
import { CellSpinner } from "./FormItems/CellSpinner";
import { FormItemType } from "./component-map";

export type Sort = (a: any, b: any) => 0 | 1 | -1;

export interface RendererParamRow {
  original: any
}
export interface RendererParams {
  value: any,
  row: RendererParamRow
}
export type Renderer = (p: RendererParams) => any;

export interface ListViewBreadcrumbs {
  name: string,
  icon: string,
  to: string
}
export enum ListViewColumnType {
  edit, delete, editdelete, link, linkEdit, datetime, yesno, date, checkbox, radio, trafficLight, icon, button, formField
}
export interface ListViewColumn {
  show?: (viewOptions: any, filtersState?: any) => boolean;
  type?: ListViewColumnType,
  Header?: string,
  id?: string,
  accessor?: string | ((row: any) => any),
  disabled?: (row: any) => boolean,
  Cell?: (row: any) => any,
  sort?: Sort,
  renderer?: Renderer,
  linkPath?: (meta: any) => string,
  newLink?: boolean,
  defaultSort?: boolean,
  defaultSortDirection?: 'asc' | 'desc',
  hidden?: boolean,
  formItemProps?: FormItem,
  validation?: any,
  hideOverflow?: boolean,
  maxWidth?: number,
  serverExpression?: string,
  excludeGlobalFilter?: boolean,
  // Suppress the per-column filter box for this column (react-table passthrough).
  disableFilters?: boolean,
  tooltip?: string | ((row: any) => any),
  disableSortBy?: boolean,
  icon?: any,
  iconColor?: string,
  buttonText?: string,
  onClick?: any,
  customPopup?: any,
  iconStyle?: any;
}
export interface ModalProps {
  form: FormView, repository: Repository, name: string, title?: string, defaults?: (row, additionalSets) => any
}
export interface ListView {
  columns: ListViewColumn[],
  filters?: FilterDefinition[],
  filterGroup?: string,
  viewOptions?: ViewOption[],
  dataSourceSelectors?: ((state: any) => any)[],
  pageHeader?: string,
  breadcrumbs?: ListViewBreadcrumbs[],
  hideGlobalFilter?: boolean,
  // When set, a filter textbox appears under each column header. Each box filters
  // only its own column (like the global search field, but scoped to that column).
  perColumnFiltering?: boolean,
  create?: FormView,
  /*   onCreate?: Repository, */
  update?: FormView,
  onUpdate?: Repository,
  adminOnly?: boolean,
  // When set, the "Add" button navigates to this route (routable create page)
  // instead of opening the create modal. Ignored when `create` is provided.
  addButtonPath?: string,
  enableExport?: boolean,
  toolbar?: any,
  variant?: 'card',
  noFetchOnMount?: boolean,
  defaultPageSize?: any,
  maxPageSize?: any,
  addButtonText?: string,
  hideAddButton?: (context: { watch?: any }) => boolean,
  selectionMode?: 'single' | 'multi',
  activeFlag?: string,
  noGlobalFilterPadding?: boolean,
  secondarySort?: string,
  // Delay (ms) after the user stops typing in the global search before querying. Default 300.
  searchDebounceMs?: number,
  // Delay (ms) after the last filter-panel change before querying (server-side grids). Default 0.
  filterDebounceMs?: number,
  // Client-side grids: on a user filter/search event, refetch if the data is older than this (ms).
  staleRefetchMs?: number,
  permissions?: any,
  customExport?: any,
  autoCheckAll?: boolean,
  disableSelection?: boolean,
  additionalModals?: ModalProps[],
  preventFetch?: boolean,
  preventRedirectOnCreate?: boolean,
  noPagingPadding?: boolean,
  onSelection?: (selection, setValue) => void,
  /*   modalSize?: "sm" | "lg" | "xl" */
}
export interface ListViewProps {
  onFilterChanged?: (filterText: string) => void;
  toolbarWidget?: ReactElement;
  onRowSelect?: (selection: any[]) => void,
  listView?: ListView,
  repository?: Repository,
  dataSelector?: any,
  fieldProps?: any,
  onSelection?: (selection, setValue) => void,
}
export enum FilterType {
  radio, combo, custom, hidden, simple, multiselect, text, number, date
};
export interface FilterProps {
  state: any, data: any[], value: any, filterState: any
}
export interface FilterDefinition {
  type: FilterType,
  fieldName: string;
  label?: string;
  options?: (state: any) => any[];
  labels?: any;
  filterValue?: any;
  number?: boolean;
  max?: number;
  serverExpression?: string;
  logicalConnector?: LogicalConnector;
  valExpression?: (val: any) => any;
  minValue?: number;
  maxValue?: number;
  // combo only: render a typeahead with partial-text search (for long lists).
  searchable?: boolean;
  // client-side grids only: send this filter's value as a request argument (refetch on change)
  // instead of filtering the loaded rows.
  requestParam?: boolean;
}
export interface ViewOption {
  type: FilterType,
  optionName: string;
  label?: string;
  options?: (state: any) => any[];
  defaultValue?: any;
}
export const connectListView = (listView: ListView, dataSource?: DataSource, dataSelector?: any) => {
  const repository = createReduxStore(dataSource);
  reduxReducer[dataSource.name] = repository.reducer;
  setReduxReducer(reduxReducer);

  return [(props?: ListViewProps) => {
    return (
      <FrameWorkListView
        onFilterChanged={props.onFilterChanged}
        toolbarWidget={props.toolbarWidget}
        onRowSelect={props.onRowSelect}
        listView={listView}
        repository={repository}
        dataSelector={dataSelector}
        fieldProps={props.fieldProps}
        onSelection={props.onSelection}
      />
    )
  }, repository]
}
export const initListView = (listView: ListView, dataSource?: DataSource, dataSelector?: any) => {
  const repository = createReduxStore(dataSource);
  reduxReducer[dataSource.name] = repository.reducer;
  setReduxReducer(reduxReducer);

  return (props?: ListViewProps) => {
    return (
      <FrameWorkListView
        onFilterChanged={props.onFilterChanged}
        toolbarWidget={props.toolbarWidget}
        onRowSelect={props.onRowSelect}
        listView={listView}
        repository={repository}
        dataSelector={dataSelector}
        fieldProps={props.fieldProps}
        onSelection={props.onSelection}
      />
    )
  }
}
export const createListView = (listView: ListView, repository?: Repository, dataSelector?: any) => {
  return (props?: ListViewProps) => {
    return (
      <FrameWorkListView
        onFilterChanged={props.onFilterChanged}
        toolbarWidget={props.toolbarWidget}
        onRowSelect={props.onRowSelect}
        listView={listView}
        repository={repository}
        dataSelector={dataSelector}
        fieldProps={props.fieldProps}
        onSelection={props.onSelection}
      />
    )

  }
}

export const FrameWorkListView = (props: ListViewProps) => {
  const { onFilterChanged, toolbarWidget, onRowSelect, listView, repository, dataSelector, fieldProps } = props;
  const { permissions, customExport, autoCheckAll, disableSelection } = listView;
  const [tableKey, setTableKey] = useState(guid());
  const redirect = useNavigate();
  const currRoles = useSelector(selectCurentUserRoles) as any;
  const writeAccess = useMemo(() => {
    if (!permissions || !permissions.write || !currRoles) return true;
    const readRoles = currRoles.find(r => permissions.write.includes(r.roleId));
    return readRoles;
  }, [currRoles, permissions]);


  const [customPopup, setCustomPopup] = useState(null as any);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);

  const CustomPopup = customPopup;

  const handlePopupCellClick = (event, row, popup) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPopupPos({
      top: rect.bottom,  // just below the cell
      left: rect.left,   // aligned with left edge
    });
    dispatch(setCurrent(row));
    setCustomPopup(popup);
  };

  const handlePopupHide = () => {
    setCustomPopup(null);
    setPopupPos(null);
  };
  useEffect(() => {
    if (!popupPos) return;
    const handler = (e: MouseEvent) => {
      const popupEl = document.getElementById("my-popup");
      if (popupEl && !popupEl.contains(e.target as Node)) {
        handlePopupHide();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [popupPos]);

  const {
    dataSourceSelectors,
    columns,
    pageHeader,
    breadcrumbs,
    hideGlobalFilter,
    perColumnFiltering,
    create,
    update,
    filters,
    filterGroup,
    adminOnly,
    enableExport,
    viewOptions,
    noFetchOnMount,
    defaultPageSize,
    maxPageSize,
    addButtonText,
    hideAddButton,
    addButtonPath,
    selectionMode,
    activeFlag,
    noGlobalFilterPadding,
    secondarySort,
    searchDebounceMs = 300,
    filterDebounceMs = 0,
    staleRefetchMs,
    additionalModals,
    preventFetch,
    preventRedirectOnCreate,
    noPagingPadding
    /*     modalSize */
  } = listView;
  const onSelection = listView.onSelection ? listView.onSelection : props.onSelection;
  const {
    dataSource: { pk, name: dataSourceName, selector, serverSideEvents, entityRequest, requestArgs, watchFields, additionalRequests, deleteRequest },
    fetchData,
    createAction,
    updateAction,
    deleteAction,
    actions: { setCurrent, setCreateStatus, setUpdateStatus, setDeleteStatus, setFilteredTotal },
    additionalActions
  } = repository;

  const data = useSelector((state: any) => state[dataSourceName].data);
  const payloadId = useSelector((state: any) => state[dataSourceName].payloadId);
  const selectedData = useSelector((state: any) => {
    return dataSelector ? dataSelector(state) : null
  });

  const [currSelection, setCurrSelection] = useState([]);

  const getData = useMemo(() => {
    return (dataSelector ? selectedData : data) || [];
  }, [selectedData, data]);

  useEffect(() => {
    const theData = getData;
    if (autoCheckAll && !disableSelection) {
      setCurrSelection(theData)
    }
    else if (disableSelection) {
      fieldProps.setValue(fieldProps.field.name, theData)
    }
  }, [getData, autoCheckAll, disableSelection, fieldProps]);
  useEffect(() => {
    if (fieldProps && fieldProps?.field?.name && !disableSelection) {
      if (selectionMode == 'single') {
        fieldProps.setValue(fieldProps.field.name, currSelection?.length ? currSelection[0][pk] : null)
        if (onSelection) onSelection(currSelection, fieldProps.setValue)
      } else {
        fieldProps.setValue(fieldProps.field.name, currSelection)
      }
    } else if (onSelection) {
      onSelection(currSelection, null)
    }
  }, [currSelection])
  const fetchStatus = useSelector((state: any) => state[dataSourceName].status);
  const { filters: urlFilters, search: urlSearch, urlKey } = useListUrlOptions(
    (filters ?? []).map(f => ({ fieldName: f.fieldName, multiselect: f.type === FilterType.multiselect })),
  );
  const [searchText, setSearchText] = useState(urlSearch);
  // Per-column filter values keyed by column id (id ?? accessor). Only used when
  // `perColumnFiltering` is enabled; server-side grids feed these into generateFilters.
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>({});
  const [pageState, setPageState] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize || 25,
  });
  const isAdminRole = useSelector(selectIsAdmin);
  const isAdmin = isAdminRole;
  // When embedded in a form, scope this list to the live form values instead of
  // redux. Calling watch(f) during render subscribes this grid to those fields
  // so it re-renders (and re-fetches, below) when they change.
  const formWatch = fieldProps?.watch;
  const watchedKey = (formWatch && watchFields?.length)
    ? JSON.stringify(watchFields.map((f: string) => formWatch(f)))
    : '';
  const requestArgsSelector = useSelector((state: any) => requestArgs ? requestArgs(state, formWatch) : null);
  const createdEntity = useSelector((state: any) => state[dataSourceName].createAction.createdEntity);
  const createError = useSelector((state: any) => state[dataSourceName].createAction.error);
  const createStatus = useSelector((state: any) => state[dataSourceName].createAction.status);
  const updateError = useSelector((state: any) => state[dataSourceName].updateAction.error);
  const updateStatus = useSelector((state: any) => state[dataSourceName].updateAction.status);
  const deleteStatus = useSelector((state: any) => state[dataSourceName].deleteAction.status);
  const [showModalAdd, setModalShowAdd] = useState(false);
  const [showModalEdit, setModalShowEdit] = useState(false);
  const [additionalModal, setShowAdditionalModal] = useState(false as (ModalProps | boolean));

  const dataStatus = useSelector((state: any) => {
    const base = state[dataSourceName];
    const status = base.status;
    return status;
  });
  const dispatch = useDispatch();

  const sharedFilterValues = useSelector((state: any) => filterGroup ? state.sharedFilters?.values?.[filterGroup] : null);
  const sharedUrlKey = useSelector((state: any) => filterGroup ? state.sharedFilters?.urlKeys?.[filterGroup] : null);
  // Assigned once filtersState exists (below); read at fetch time.
  const requestParamArgsRef = useRef<any>(null);

  const loadData = useCallback(async () => {
    if (preventFetch) return;
    if (serverSideEvents) {
      dispatch(fetchData({
        page: pageState.pageIndex,
        limit: pageState.pageSize,
        sortDefinition: getSort(),
        filters: generateFilters(),
      }));
    } else {
      dispatch(fetchData(requestParamArgsRef.current ?? requestArgsSelector));
    }

    /*       } */
  }, [dataStatus, dispatch]);

  useEffect(() => {
    if (additionalActions && additionalRequests && data?.length) {
      additionalRequests.forEach(act => {
        dispatch(additionalActions[act.name](getData));
      })
    }
  }, [payloadId]);

  useEffect(() => {
    if (!noFetchOnMount || fetchStatus == 'idle') {
      loadData();
    }
  }, []);

  const lastFetchedAt = useRef(0);
  useEffect(() => {
    if (payloadId) lastFetchedAt.current = Date.now();
  }, [payloadId]);
  const refetchIfStale = () => {
    if (!staleRefetchMs || serverSideEvents || !lastFetchedAt.current) return;
    if (Date.now() - lastFetchedAt.current < staleRefetchMs) return;
    // Restart the window now so repeated events don't refetch while this one is in flight.
    lastFetchedAt.current = Date.now();
    loadData();
  };
  // The debounced search handler is created once, so it reads the latest closure via this ref.
  const refetchIfStaleRef = useRef(refetchIfStale);
  refetchIfStaleRef.current = refetchIfStale;

  const tabActive = useContext(TabActiveContext);
  useEffectDelta(() => {
    if (tabActive) refetchIfStale();
  }, [tabActive]);

  // Refetch when the form fields this grid is scoped by change (e.g. the create
  // form's customer/location). Skipped on first render (mount handled above) and
  // for server-side grids (driven by the page/sort/filter effects).
  useEffectDelta(() => {
    if (preventFetch || serverSideEvents) return;
    dispatch(fetchData(requestArgsSelector));
  }, [watchedKey]);

  const tableInstance = useRef(null);
  const tableRef = tableInstance.current;

  const initFilterState = React.useMemo(() => filters?.reduce((obj, current, idx) => {
    switch (current.type) {
      case FilterType.text:
      case FilterType.number:
      case FilterType.date:
        obj[current.fieldName] = current.filterValue;
        break;
      case FilterType.combo:
        obj[current.fieldName] = current.filterValue;
        break;
      case FilterType.multiselect:
        obj[current.fieldName] = current.filterValue;
        break;
      default:
        obj[current.fieldName] = current.filterValue ?? true;
    }
    // Seed from a shared filter group so filters stay consistent across sibling views (e.g. tabs)
    if (filterGroup && sharedFilterValues && current.fieldName in sharedFilterValues) {
      obj[current.fieldName] = sharedFilterValues[current.fieldName];
    }
    if ((!filterGroup || sharedUrlKey !== urlKey) && Object.prototype.hasOwnProperty.call(urlFilters, current.fieldName)) {
      obj[current.fieldName] = urlFilters[current.fieldName];
    }
    return obj;
  }, {}), [filters]);

  const initViewOptionState = React.useMemo(() => viewOptions?.reduce((obj, current, idx) => {
    obj[current.optionName] = current.defaultValue;
    return obj;
  }, {}), [viewOptions]);


  const handleFilterChange = (value, filterId) => {
    const filter = filters?.find(f => f.fieldName === filterId);
    // requestParam filters refetch on change anyway.
    if (!filter?.requestParam) refetchIfStale();
    switch (filter?.type) {
      case FilterType.simple:
      case FilterType.radio:
        if (!serverSideEvents) {
          tableRef.setFilter(filterId, !filtersState[filterId]);
        }
        dispatchFilters({ type: filterId });
        if (filterGroup) dispatch(setSharedFilter({ group: filterGroup, fieldName: filterId, value: !filtersState[filterId] }));
        return;
      case FilterType.multiselect:
      case FilterType.combo:
        dispatchFilters({ type: filterId, value: value });
        if (filterGroup) dispatch(setSharedFilter({ group: filterGroup, fieldName: filterId, value: value }));
        return;
      case FilterType.number:
      case FilterType.text:
      case FilterType.date:
        dispatchFilters({ type: filterId, value: value });
        if (filterGroup) dispatch(setSharedFilter({ group: filterGroup, fieldName: filterId, value: value }));
        return;
      default:
        return;
    }
  };
  const handlerViewOptionChange = (value, filterId) => {
    dispatchViewOptions({ type: filterId });
  };

  function filtersReducer(state, action) {
    const fieldName = action.type;
    const setVal = Object.prototype.hasOwnProperty.call(action, 'value') ? action.value : !state[action.type];
    const filter = filters?.find(f => f.fieldName === fieldName);
    switch (typeof filter?.type == 'undefined' ? FilterType.simple : filter?.type) {
      case FilterType.simple:
      case FilterType.radio:
        return { ...state, [fieldName]: setVal };
      case FilterType.multiselect:
      case FilterType.combo:
        return { ...state, [fieldName]: action.value };
      case FilterType.text:
      case FilterType.number:
      case FilterType.date:
        return { ...state, [fieldName]: action.value };
      default:
        return;
    }
  };
  function viewOptionsReducer(state, action) {
    const fieldName = action.type;
    const setVal = action.value ?? !state[action.type];
    const filter = viewOptions?.find(f => f.optionName === fieldName);
    switch (typeof filter?.type == 'undefined' ? FilterType.simple : filter?.type) {
      case FilterType.simple:
      case FilterType.radio:
        return { ...state, [fieldName]: setVal };
      case FilterType.multiselect:
      case FilterType.combo:
        return { ...state, [fieldName]: action.value };
      default:
        return;
    }
  };
  const [filtersState, dispatchFilters] = useReducer(filtersReducer, { filterOpen: false, ...initFilterState });
  const [viewOptionsState, dispatchViewOptions] = useReducer(viewOptionsReducer, { viewOptionsOpen: false, ...initViewOptionState });

  useEffect(() => {
    if (filterGroup) {
      dispatch(initializeUrlFilters({ group: filterGroup, urlKey, values: urlFilters }));
    } else {
      filters?.forEach(f => {
        if (Object.prototype.hasOwnProperty.call(urlFilters, f.fieldName)) {
          dispatchFilters({ type: f.fieldName, value: urlFilters[f.fieldName] });
        }
      });
    }
  }, [urlKey, filterGroup, urlFilters, dispatch, filters]);

  const requestParamFilters = (serverSideEvents ? [] : filters ?? []).filter(f => f.requestParam);
  const requestParamArgs = requestParamFilters.length
    ? requestParamFilters.reduce((obj, f) => ({ ...obj, [f.fieldName]: filtersState[f.fieldName] }), {})
    : null;
  requestParamArgsRef.current = requestParamArgs;

  useEffectDelta(() => {
    if (preventFetch || !requestParamArgs) return;
    dispatch(fetchData(requestParamArgs));
  }, [JSON.stringify(requestParamArgs)]);

  // Keep this view's filters in sync when a sibling view (e.g. another tab) changes a shared filter.
  useEffect(() => {
    if (!filterGroup || !sharedFilterValues || sharedUrlKey !== urlKey) return;
    filters?.forEach((f) => {
      if (!(f.fieldName in sharedFilterValues)) return;
      const nextVal = sharedFilterValues[f.fieldName];
      if (JSON.stringify(nextVal) !== JSON.stringify(filtersState[f.fieldName])) {
        dispatchFilters({ type: f.fieldName, value: nextVal });
      }
    });
  }, [JSON.stringify(sharedFilterValues), sharedUrlKey, urlKey]);

  const setDefaultFilters = () => {
    Object.keys(filtersState).forEach((filterId) => {
      const filter = filters?.find(f => f.fieldName === filterId);
      if (filterId !== 'filterOpen' && filtersState[filterId] === true) {
        if (tableRef != null) {
          if (!serverSideEvents) {
            tableRef.setFilter(filterId, typeof filter.filterValue == 'undefined' ? false : filter.filterValue);
          }
        }
      }
      // Combo/multiselect values are applied from live filter state via clientTableFilters.
    });
  };

  const tableColumns = useMemo(
    () => columns
    ,
    [],
  );

  const additionalSets = useSelector((state: any) => {
    const sets = {};
    additionalRequests?.forEach(req => {
      const isLoading = state[dataSourceName][req.name].status == 'loading';
      const d = state[dataSourceName][req.name].data;
      sets[req.name] = isLoading ? null : d;
    });
    return sets;
  });

  const total = useSelector((state: any) => state[dataSourceName].total);
  const numPages = Math.ceil((total ?? 0) / (pageState?.pageSize ?? 1));

  useEffect(() => {
    setDefaultFilters();
  }, [tableRef, getData]);

  const createSuccessNotify = () => toast.success('Successfully Saved', { id: 'savesuccess' });
  const createFailureNotify = (error) => toast.error(error ? `Failed to Save: ${error}` : `Failed to Save`, { id: 'savefailure' });

  useEffect(() => {
    if (createStatus === 'succeeded') {
      if (create) {
        createSuccessNotify()
        dispatch(setCreateStatus('idle'));
        setModalShowAdd(false);
        const c = columns?.find(c => c?.type == ListViewColumnType.linkEdit && c?.linkPath);
        if (c && !preventRedirectOnCreate) {
          const editPath = c?.linkPath({ value: createdEntity[(c?.id || c?.accessor) as string], row: { original: createdEntity } });
          if (editPath) {
            redirect(editPath);
          }
        }
      }
    } else if (createStatus === 'failed') {
      createFailureNotify(createError);
      dispatch(setCreateStatus('idle'));
    }
    if (updateStatus === 'succeeded') {
      if (update) {
        createSuccessNotify();
        dispatch(setUpdateStatus('idle'));
        setModalShowEdit(false);
      }
    } else if (updateStatus === 'failed') {
      createFailureNotify(updateError);
      dispatch(setUpdateStatus('idle'));
    }
    if(deleteRequest && deleteAction && deleteStatus === 'succeeded') {
      dispatch(setDeleteStatus('idle'));
      loadData();
    }
  }, [createStatus, updateStatus, deleteStatus, createdEntity, createError, updateError]);

  const {
    datePreference
  } = useSelector(selectLoggedInUserPreferences) as any;

  const validationMsg = (message, row) => {
    if (typeof message == 'function') {
      return message(row)
    }
    return message;
  }
  const validate = (row, colDef, content) => {
    if (content == 'loading...') return content;
    if (colDef.validation) {
      const failedValidations = Object.keys(colDef.validation).filter(v => {
        const { handler } = colDef.validation[v];
        const passes = handler(row);
        return !passes;
      }).map(v => ({ validator: v, message: validationMsg(colDef.validation[v].message, row), color: colDef.validation[v].color }));
      const passed = !failedValidations?.length;
      const title = !passed ? failedValidations[0].message : (colDef.tooltip && typeof colDef.tooltip == 'function' ? colDef.tooltip(row) : undefined);
      return (
        <div style={{ color: !passed ? (failedValidations[0].color || 'red') : undefined }} title={title}>
          {content}
        </div>
      )
    }
    return content;
  }

  const parseColumns = (cols) => {
    return cols.map(col => {
      if (col.type === ListViewColumnType.trafficLight) {
        return {
          ...col,
          Cell: ({ value, row }) => {
            const status = value;
            if (status === 'Red') {
              return <RedIcon tooltip={col.tooltip && col.tooltip(row.original)} />;
            }
            if (status === 'Yellow') {
              return <YelloIcon tooltip={col.tooltip && col.tooltip(row.original)} />;
            }
            if (status === 'Green') {
              return <GreenIcon tooltip={col.tooltip && col.tooltip(row.original)} />;
            }
            return '';
          },
        }
      }
      if (col.type === ListViewColumnType.button) {
        return {
          ...col,
          fixedWidth: 52,
          disableSortBy: true,
          Cell: ({ value, row }) => {
            const status = value;
            const ColIcon = typeof col.icon == 'function' ? col.icon(row, additionalSets) : col.icon;
            return (
              <Button
                size={"xsm" as any}
                title={col.tooltip}
                variant={col.variant && col.variant(row, additionalSets)}
                disabled={!writeAccess || (col.disabled ? col.disabled(row, additionalSets) : undefined)}
                onClick={async () => {
                  if (col.onClick) {
                    col.onClick()
                  }
                  if (col.modal) {
                    const theModal = additionalModals.find(m => m.name == col.modal(row, additionalSets));
                    if (theModal) {
                      const { actions: { setCurrent } } = (theModal as ModalProps).repository;
                      if ((theModal as ModalProps).defaults) {
                        const defs = await (theModal as ModalProps).defaults(row, additionalSets);
                        dispatch(setCurrent(defs));
                      }
                      setShowAdditionalModal(theModal)
                    }
                  }
                }}
                className="mr-1"
              >
                {ColIcon ? <ColIcon /> : col.buttonText}
              </Button>
            )
            return '';
          },
        }
      }
      if (col.type === ListViewColumnType.icon) {
        return {
          ...col,
          fixedWidth: 52,
          Cell: ({ value, row }) => {
            const status = value;
            return (
              <i
                className={`bi bi-${status}`}
                title={col.tooltip && col.tooltip(row.original)}
                onClick={(e) => col.customPopup ? handlePopupCellClick(e, row.original, col.customPopup) : null}
                style={col.iconStyle && col.iconStyle(row.original)}
              />
            )
          },
        }
      }
      if (col.type === ListViewColumnType.radio) {
        return {
          ...col,
          Cell: ({ value, row }) => <input type="radio" style={{ transform: 'scale(1.2)' }} checked={currSelection?.length && currSelection[0][pk] === row.original[pk]} onChange={(e) => {
            setCurrSelection([row.original])
          }} disabled={col.disabled ? col.disabled(row.original) : false} />,
        }
      }
      if (col.type === ListViewColumnType.checkbox) {
        return {
          ...col,
          disableSortBy: true,
          fixedWidth: 52,
          Cell: ({ value, row }) => (
            <input
              type="checkbox"
              style={{ transform: 'scale(1.2)' }}
              disabled={col.disabled ? col.disabled(row.original) : false}
              checked={currSelection?.length && currSelection.find(r => r[pk] == row.original[pk])}
              onChange={(e) => {
                if (e.target.checked) {
                  setCurrSelection([...currSelection, row.original])
                } else {
                  setCurrSelection([...currSelection.filter(r => r[pk] != row.original[pk])])
                }
                //setCurrSelection([...currSelection, row.original])
              }}
            />
          ),
          Header: () => {
            return (
              <input
                type="checkbox"
                style={{ transform: 'scale(1.2)' }}
                checked={(getData).filter(r => !col.disabled || !col.disabled(r))?.length == currSelection?.length && currSelection?.length >= 1}
                onChange={(e) => {
                  const myData = (getData);
                  const enabledRows = myData.filter(r => !col.disabled || !col.disabled(r))
                  if (e.target.checked) {
                    //select all
                    setCurrSelection([...enabledRows])
                  } else {
                    //deselect all
                    setCurrSelection([])
                  }
                }}
              />
            )
          }
        }
      }
      if (col.type === ListViewColumnType.yesno) {
        return {
          ...col,
          Cell: ({ value, row }) => value ? 'Yes' : 'No',
        }
      }
      if (col.type === ListViewColumnType.datetime) {
        return {
          ...col,
          accessor: typeof col.accessor == 'function' ? (row) => {
            return col.accessor(row, additionalSets)
          } : col.accessor,
          Cell: ({ value, row }) => {
            return validate({ ...row.original, ...row.values }, col, value == 'loading...' ? value : !value ? '' : formatDate(setDateAsUTC(value), datePreference));
          }
        }
      }
      if (col.type === ListViewColumnType.date) {
        return {
          ...col,
          accessor: typeof col.accessor == 'function' ? (row) => {
            return col.accessor(row, additionalSets)
          } : col.accessor,
          Cell: ({ value, row }) => {
            return validate({ ...row.original, ...row.values }, col, value == 'loading...' ? value : !value ? '' : formatDate(value, datePreference));
          }
        }
      }
      if (col.type === ListViewColumnType.link) {
        return {
          ...col,
          accessor: typeof col.accessor == 'function' ? (row) => {
            return col.accessor(row, additionalSets)
          } : col.accessor,
          Cell: ({ value, row }) => (<NavLink target={col.newLink && "_blank"} to={col.linkPath({ value, row })}>{col.icon ?? value}</NavLink>),
        };
      }
      if (col.type === ListViewColumnType.linkEdit) {
        return {
          ...col,
          Cell: ({ value, row }) => (<NavLink onClick={() => {
            if (!entityRequest) {
              dispatch(setCurrent(row.original));
            } else {
              dispatch(setCurrent(null));
            }
          }} to={col.linkPath({ value, row })}>{value}</NavLink>),
        };
      }
      if (col.type === ListViewColumnType.delete) {
        return {
          Header: '',
          id: 'deleteColumn',
          fixedWidth: 52,
          Cell: ({ cell }) => (
            <div className="d-flex">
              <Button
                disabled={!writeAccess}
                // @ts-ignore
                size="xsm"
                variant={cell.row.original.isActive || deleteRequest ? 'danger' : 'success'}
                title={cell.row.original.isActive || deleteRequest ? 'Deactivate' : 'Activate'}
                onClick={() => {
                  if (deleteRequest) {
                    if (confirm("Are you sure you want to permanently delete?") == true) {
                      dispatch(deleteAction(cell.row.original[pk]));
                    }
                  } else {
                    if (cell.row.original.isActive) {
                      if (confirm("Are you sure you want to deactivate?") == true) {
                        dispatch(updateAction({ ...cell.row.original, isActive: false }))
                      }
                    } else {
                      dispatch(
                        updateAction({ ...cell.row.original, isActive: true })
                      )
                    }
                  }

                }}
              >
                {cell.row.original.isActive || deleteRequest ? <BsXLg /> : <BsPlusLg />}
              </Button>
            </div>
          ),
        };
      }
      if (col.type === ListViewColumnType.edit) {
        return {
          Header: '',
          id: 'actionColumn',
          fixedWidth: 52,
          Cell: ({ cell }) => (
            <div className="d-flex">
              <Button
                // @ts-ignore
                size="xsm"
                title="Edit"
                disabled={!writeAccess}
                onClick={() => {
                  dispatch(setCurrent(cell.row.original));
                  setModalShowEdit(true);
                }}
              >
                <BsPencilFill />
              </Button>
            </div>
          ),
        };
      }
      if (col.type === ListViewColumnType.editdelete) {
        return {
          Header: '',
          id: 'actionColumn',
          fixedWidth: 52,
          Cell: ({ cell }) => (
            <div className="d-flex">
              <Button
                // @ts-ignore
                size="xsm"
                title="Edit"
                disabled={!writeAccess}
                onClick={() => {
                  dispatch(setCurrent(cell.row.original));
                  setModalShowEdit(true);
                }}
              >
                <BsPencilFill />
              </Button>
              &nbsp;
              <Button
                // @ts-ignore
                size="xsm"
                disabled={!writeAccess}
                variant={(activeFlag ? cell.row.original[activeFlag] : cell.row.original.isActive) ? 'danger' : 'success'}
                title={(activeFlag ? cell.row.original[activeFlag] : cell.row.original.isActive) ? 'Deactivate' : 'Activate'}
                onClick={() => ((activeFlag ? cell.row.original[activeFlag] : cell.row.original.isActive)
                  ? dispatch(updateAction({ ...cell.row.original, [activeFlag ? activeFlag : 'isActive']: false }))
                  : dispatch(
                    updateAction({ ...cell.row.original, [activeFlag ? activeFlag : 'isActive']: true })
                  ))}
              >
                {(activeFlag ? cell.row.original[activeFlag] : cell.row.original.isActive) ? <BsXLg /> : <BsPlusLg />}
              </Button>
            </div>
          ),
        };
      } else if (col.validation) {
        return {
          ...col,
          accessor: typeof col.accessor == 'function' ? (row) => {
            return col.accessor(row, additionalSets)
          } : col.accessor,
          Cell: (args) => {
            return validate({ ...args.row.original, ...args.row.values }, col, col.Cell ? col.Cell(args) : args.value);
          },
          filter: hasMultiFilter(col) ? (rows: any[], columnIds: string[], filterValue) => {
            const id = col.id ?? col.accessor;
            return rows.filter(row => filterValue[row.values[id]]);
          } : col.filter
        }
      }
      const overflowHide = col.hideOverflow ? { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden', maxWidth: col.maxWidth ? col.maxWidth : '50px' } : {};
      return (
        {
          ...col,
          ...(col.Cell ? { Cell: (({ value, row }) => (!col.loading || !col.loading({ value, row }, additionalSets) ? col.Cell({ value, row }, additionalSets) : <CellSpinner />)) } : {}),
          title: col.hideOverflow ? (c, b) => {
            return typeof col.accessor == 'function' ? col.accessor(b.original, additionalSets) : b.values[col.accessor];
          } : (col.tooltip ? (c, b) => col.tooltip(b.values) : col.title),
          style: {
            ...col.style,
            ...overflowHide
          },
          accessor: typeof col.accessor == 'function' ? (row) => {
            return col.accessor(row, additionalSets)
          } : col.accessor,
          filter: hasMultiFilter(col) ? (rows: any[], columnIds: string[], filterValue) => {
            const id = col.id ?? col.accessor;
            return rows.filter(row => filterValue[row.values[id]]);
          } : col.filter
        }
      );
    })
  }

  // filterOpen only toggles the panel; exclude it so opening filters doesn't remount the grid.
  const { filterOpen: _filterOpen, ...filterVals } = filtersState;
  const hiddenColumns = useMemo(() => columns.filter(c => c.hidden || (c.show && !c.show(viewOptionsState, filtersState))).map(h => (h.id ?? h.accessor)), [columns, viewOptionsState, JSON.stringify(filterVals)]);

  useEffect(() => {
    setTableKey(guid());
  }, [JSON.stringify(hiddenColumns)]);

  const hasMultiFilter = (col: any) => {
    const id = col.id ?? col.accessor;
    return filters?.find(f => f.fieldName === id)?.type == FilterType.multiselect;
  }

  // Client-side grids: mirror filter-panel values into react-table. Only filters backed by a
  // column apply (react-table throws otherwise); seeded via initialState to survive remounts.
  const clientTableFilters = useMemo(() => {
    if (serverSideEvents) return [];
    const valueTypes = [FilterType.multiselect, FilterType.combo, FilterType.text, FilterType.number];
    return (filters ?? [])
      .filter(f => valueTypes.includes(f.type) && !f.requestParam && columns.some(c => (c.id ?? c.accessor) === f.fieldName))
      .map(f => {
        const v = filtersState[f.fieldName];
        return { id: f.fieldName, value: v === '' || v === null ? undefined : v };
      });
  }, [serverSideEvents, JSON.stringify(filterVals)]);

  useEffect(() => {
    const table = tableInstance.current;
    if (!table) return;
    clientTableFilters.forEach(f => table.setFilter(f.id, f.value));
  }, [clientTableFilters]);

  const parsedColumns = useMemo(() => {
    return parseColumns(tableColumns);
  }, [tableColumns, additionalSets]);

  const defaultSortCol = parsedColumns.find(x => x.defaultSort);
  const defaultSort = defaultSortCol?.id || defaultSortCol?.accessor;
  const defaultSortDirection = parsedColumns.find(x => x.defaultSort)?.defaultSortDirection;
  const [sortState, setSortState] = useState([{
    id: defaultSort,
    desc: defaultSortDirection == 'desc'
  }]);

  const tableProps = {
    key: tableKey,
    onPaginationChange: (args) => {
      setPageState(args);
      if (args.pageIndex != pageState.pageIndex || args.pageSize != pageState.pageSize) {
        dispatch(fetchData({
          sortDefinition: getSort(),
          filters: generateFilters(),
          page: args.pageIndex,
          limit: args.pageSize
        }));
      }
    },
    onSortingChange: (args) => {
      if (serverSideEvents && args?.length === 0) {
        setSortState([{
          id: defaultSort,
          desc: defaultSortDirection == 'desc'
        }])
      } else {
        setSortState(args);
      }
    },
    defaultPageSize: defaultPageSize,
    maxPageSize: maxPageSize,
    pageCount: serverSideEvents ? numPages : undefined,
    manualSorting: serverSideEvents,
    manualPagination: serverSideEvents,
    manualGlobalFilter: serverSideEvents,
    noPagingPadding: noPagingPadding,
    onDataFiltered: (rows) => {
      if (!serverSideEvents && setFilteredTotal) dispatch(setFilteredTotal(rows?.length ?? 0));
      if (onFilterChanged) onFilterChanged(rows);
    },
    onRowSelect: onRowSelect,
    singleSelectMode: true,
    columns: parsedColumns,
    data: getData,
    ref: tableInstance,
    perColumnFiltering: perColumnFiltering,
    columnFilters: columnFilters,
    onColumnFilterChanged: (columnId, value) => onColumnFilterChange(columnId, value),
    rowProps: () => ({}),
    initialState: {
      hiddenColumns: hiddenColumns,
      globalFilter: searchText,
      filters: clientTableFilters.filter(f => f.value !== undefined),
      sortBy: defaultSort ? [
        {
          id: defaultSort,
          desc: defaultSortDirection == 'desc'
        }
      ] : []
    },
  };
  const table = (
    onRowSelect ?
      <RowSelectTable
        {...tableProps}
      /> :
      <PaginatedSelectTable
        {...tableProps}
      />
  );
  const breadcrumbsMemo = useMemo(() => breadcrumbs, []);

  const toggleModalAdd = () => {
    setModalShowAdd(true);
    dispatch(setCurrent(null));
  }
  const onGlobalSearch = (newSearchText) => {
    setSearchText(newSearchText);
    if (!serverSideEvents) {
      refetchIfStaleRef.current();
      return;
    }
  };
  const debouncedChangeHandler = useCallback(
    debounce(onGlobalSearch, searchDebounceMs)
    , []);

  useEffect(() => {
    debouncedChangeHandler.cancel();
    setSearchText(urlSearch);
    tableInstance.current?.setGlobalFilter(urlSearch);
    return () => debouncedChangeHandler.cancel();
  }, [urlSearch, debouncedChangeHandler]);

  // Per-column filter change. Server-side grids stash the value and refetch (see the
  // useEffectDelta below); client-side grids apply react-table's built-in column filter.
  const onColumnFilterChange = (columnId: string, value: string) => {
    if (serverSideEvents) {
      setColumnFilters(prev => {
        const next = { ...prev };
        if (value) next[columnId] = value; else delete next[columnId];
        return next;
      });
    } else {
      tableInstance?.current?.setFilter(columnId, value || undefined);
    }
  };

  const generateFilters = () => {
    let filtersArray = [] as QueryFilter[];
    if (searchText && searchText.length > 2) {
      let globalFilter = columns.filter(c => !c.excludeGlobalFilter).map(column => ({
        accessor: column.serverExpression || column.accessor,
        logicalConnector: LogicalConnector.Or,
        value: searchText,
        logicalOperator: LogicalOperator.Like
      })) as QueryFilter[];
      filtersArray.push({
        filters: globalFilter
      });
    }
    // Per-column search boxes: one Like filter per populated column, AND-combined with
    // the global search and the filter-panel filters (mirrors the global search mapping).
    Object.keys(columnFilters).forEach(columnId => {
      const value = columnFilters[columnId];
      if (!value || value.length === 0) return;
      const column = columns.find(c => (c.id ?? c.accessor) === columnId);
      if (!column) return;
      filtersArray.push({
        accessor: column.serverExpression || column.accessor,
        logicalConnector: LogicalConnector.And,
        value,
        logicalOperator: LogicalOperator.Like
      } as QueryFilter);
    });
    let filterCount = 0;
    Object.keys(filtersState).filter(f => f != 'filterOpen').forEach((filter, fIdx) => {
      const filterDefinition = filters.find(f => f.fieldName == filter);
      if (filterDefinition.type == FilterType.multiselect) {
        const filterValue = filtersState[filter];
        let multiFilter = Object.keys(filterValue).filter(k => filterValue[k]).map(d => ({
          accessor: filterDefinition.fieldName,
          logicalConnector: LogicalConnector.Or,
          value: d,
          logicalOperator: LogicalOperator.Equal
        }))
        if (multiFilter?.length > 0) {
          filtersArray.push({
            filters: multiFilter,
            /*             logicalConnector: LogicalConnector.And, */
          });
        }
      } else {
        const filterValue = filtersState[filter];
        if (filterValue) {
          filtersArray.push({
            accessor: filterDefinition.serverExpression ? getServerExpression(filterDefinition, filterValue) : filter,
            logicalConnector: typeof filterDefinition.logicalConnector != 'undefined' && filterCount != 0 ? filterDefinition.logicalConnector : LogicalConnector.And,
            value: filterValue,
            logicalOperator: LogicalOperator.Equal,
            stringExpression: !!filterDefinition.serverExpression
          });
          filterCount++;
        }
      }
    })
    return filtersArray;
  }

  const getServerExpression = (filter: FilterDefinition, value: any) => {
    if (filter.type == FilterType.text || filter.type == FilterType.number || filter.type == FilterType.combo) {
      return filter.serverExpression.replace("@val", filter.valExpression ? filter.valExpression(value) : value);
    }
    return filter.serverExpression;
  }

  const getSort = () => {
    if (sortState?.length) {
      const sortId = sortState[0].id;
      if (!sortId) return undefined;
      const sortCol = columns.find(c => (c.id || c.accessor) === sortId);
      const sortExpression = sortCol?.serverExpression || sortId;
      return (
        {
          path: sortExpression,
          direction: sortState[0].desc ? SortDirection.Desc : SortDirection.Asc,
          secondarySort
        }
      );
    }
    return undefined;
  }

  // Search terms of 1-2 chars don't filter (see generateFilters), so they must not trigger a query.
  const effectiveSearchText = searchText && searchText.length > 2 ? searchText : '';
  const filterValuesKey = JSON.stringify(Object.keys(filtersState).filter(f => f != 'filterOpen').map(f => filtersState[f]));
  const sortKey = JSON.stringify(sortState);
  const lastFilterValuesKey = useRef(filterValuesKey);
  const lastSortKey = useRef(sortKey);
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(refetchTimer.current), []);

  useEffectDelta(() => {
    setCurrSelection([])
    if (!serverSideEvents)
      return;

    const filtersChanged = lastFilterValuesKey.current !== filterValuesKey;
    const sortChanged = lastSortKey.current !== sortKey;
    lastFilterValuesKey.current = filterValuesKey;
    lastSortKey.current = sortKey;

    const refetch = () => {
      tableInstance?.current.gotoPage(0);
      setPageState({
        pageIndex: 0,
        pageSize: pageState.pageSize
      })

      dispatch(fetchData({
        sortDefinition: getSort(),
        filters: generateFilters(),
        page: 0,
        limit: pageState.pageSize
      }));
    };

    // Rapid filter toggles collapse into one query; any other change supersedes a pending one.
    clearTimeout(refetchTimer.current);
    if (filtersChanged && !sortChanged && filterDebounceMs > 0) {
      refetchTimer.current = setTimeout(refetch, filterDebounceMs);
    } else {
      refetch();
    }
  }, [effectiveSearchText, filterValuesKey, sortKey, JSON.stringify(columnFilters)])

  const renderFilter = (filter: FilterDefinition, key: number) => {
    switch (filter.type) {
      case FilterType.simple:
      case FilterType.radio:
        return (
          <Form.Check
            key={key}
            type="switch"
            className="mb-1"
            inline
            label={filter.label}
            checked={filtersState[filter.fieldName]}
            onChange={(e) => handleFilterChange(e, filter.fieldName)}
          />
        );
      case FilterType.combo:
        return (<SelectFilter defaultValue={filtersState[filter.fieldName]} data={getData} key={filterGroup ? `${key}-${filtersState[filter.fieldName]}` : key} filter={filter} handleFilterChange={handleFilterChange} selector={filter.options} />);
      case FilterType.multiselect:
        return (<MultiSelectFilter key={key} filtersState={filtersState} filter={filter} handleFilterChange={handleFilterChange} selector={filter.options} />);
      case FilterType.text:
      case FilterType.number:
      case FilterType.date:
        return (
          <Row className="uiRow">
            <div style={{ display: 'inline-flex' }}>
              {filter.label && <Form.Label style={{ marginTop: 6, marginRight: 6, whiteSpace: 'nowrap' }}>{filter.label}</Form.Label>}
              <Form.Control size="sm" max={filter.maxValue} min={filter.minValue} type={filter.type == FilterType.number ? "number" : filter.type == FilterType.date ? "date" : undefined} key={key} defaultValue={filtersState[filter.fieldName]} onChange={debounce((e) => handleFilterChange(e.target.value, filter.fieldName), 300)} />
            </div>
          </Row>
        )
      default:
        throw new Error('invalid case');
    }
  }
  const renderViewOption = (viewOption: ViewOption, key: number) => {
    switch (viewOption.type) {
      case FilterType.simple:
      case FilterType.radio:
        return (
          <Form.Check
            key={key}
            type="switch"
            className="mb-1"
            inline
            label={viewOption.label}
            checked={viewOptionsState[viewOption.optionName]}
            onChange={(e) => handlerViewOptionChange(e, viewOption.optionName)}
          />
        );
      default:
        throw new Error('invalid case');
    }
  }

  const onExport = () => {
    const rows = tableRef?.globalFilteredRows?.map(r => r.original);

    if (customExport) {
      let headers = Object.keys(rows[0]) as any;
      const orderedHeaders = [...new Set(parsedColumns.filter((h) => typeof h.accessor === 'function' || headers.includes(h.id ?? h.accessor)).map((col) => col.id ?? col.accessor))];
      headers = [
        ...orderedHeaders,
        ...headers.filter((h) => !orderedHeaders.includes(h)),
      ];
      const mergedData = mergeData(rows, headers, parsedColumns, additionalSets);
      customExport(mergedData);
      return;
    }
    exportData('data', rows, parsedColumns, additionalSets);
  };

  const isField = noGlobalFilterPadding;

  const toolBarContent = (
    <>
      <div className="card-tools" style={!isField ? undefined : { marginLeft: 0, marginRight: 0, marginTop: 0 }}>
        {(!hideGlobalFilter || create || addButtonPath) && <div className="card-tools" style={!isField ? undefined : { marginLeft: 0, marginRight: 0, marginTop: 0 }}>

          <InputGroup size="sm">
            <>
              {!hideGlobalFilter && <>
                {!filters && <InputGroup.Text><i className="bi bi-search fas" /></InputGroup.Text>}
                {filters && <Button
                  data-cy="open-filters"
                  variant={filtersState.filterOpen ? 'primary' : 'outline-primary'}
                  onClick={() => {
                    dispatchFilters({ type: 'filterOpen' })
                    dispatchViewOptions({ type: 'viewOptionsOpen', value: false })
                  }}
                >
                  <i className={filtersState.filterOpen ? 'bi bi-funnel-fill' : 'bi bi-funnel'} />
                </Button>}
                {viewOptions && <Button
                  data-cy="open-viewoptions"
                  variant={viewOptionsState.viewOptionsOpen ? 'primary' : 'outline-primary'}
                  onClick={() => {
                    dispatchViewOptions({ type: 'viewOptionsOpen', value: !viewOptionsState.viewOptionsOpen })
                    dispatchFilters({ type: 'filterOpen', value: false })
                  }}
                >
                  <i className={'bi-sliders2'} />
                </Button>}
                <TableSearchFilter key={urlSearch} defaultValue={urlSearch} onFilterChanged={debouncedChangeHandler} tableInstance={tableInstance} />
              </>}
              {enableExport && <Button variant="primary" size="sm" onClick={onExport}><i className="bi bi-download" /></Button>}
              {props.listView.toolbar && props.listView.toolbar?.map(btn => {
                if (btn.component) {
                  const ToolbarComponent = btn.component;
                  return <ToolbarComponent key={btn.label ?? 'toolbar-component'} loadData={loadData} />;
                }
                if (btn.type == FormItemType.button && btn.modal) {
                  const TheButton = fieldProps.renderItem({
                    ...btn,
                    handler: () => {
                      const theModal = additionalModals.find(m => m.name == btn.modal);
                      if (theModal) {
                        setShowAdditionalModal(theModal)
                      }
                    }
                  });
                  return TheButton;
                }
                else if (fieldProps) {
                  return fieldProps.renderItem({ ...btn, loadData });
                }
                return (
                  <Button variant="primary" size="sm" onClick={() => btn.handler({ loadData })}>{btn.label}</Button>
                );
              })}
              &nbsp;
              {create && !hideAddButton?.({ watch: fieldProps?.watch }) && (!adminOnly || isAdmin) && <Button disabled={!writeAccess} variant="primary" size="sm" onClick={toggleModalAdd}>{addButtonText || 'Add'}</Button>}
              {!create && addButtonPath && !hideAddButton?.({ watch: fieldProps?.watch }) && (!adminOnly || isAdmin) && <Button disabled={!writeAccess} variant="primary" size="sm" onClick={() => redirect(addButtonPath)}>{addButtonText || 'Add'}</Button>}
              {toolbarWidget ? toolbarWidget : undefined}
            </>
          </InputGroup>
          {filters?.length && <Collapse in={filtersState.filterOpen}>
            <div>
              <div className="p-3 d-flex flex-wrap wrap">
                {
                  filters.map((f, idx) => renderFilter(f, idx))
                }
              </div>
            </div>
          </Collapse>}
          {viewOptions?.length && <Collapse in={viewOptionsState.viewOptionsOpen}>
            <div>
              <div className="p-3 d-flex flex-wrap wrap">
                {
                  viewOptions.map((f, idx) => renderViewOption(f, idx))
                }
              </div>
            </div>
          </Collapse>}
        </div>}
      </div>
      {table}
    </>
  );


  const container = (
    <>
      {pageHeader && <PageHeader header={pageHeader} breadcrumbs={breadcrumbsMemo} />}
      <div className={`d-flex flex-column flex-grow-1 h-100`}>
        {
          props.listView.variant == 'card' ? (
            <div className={`d-flex flex-column flex-grow-1 h-100`}>
              <Card className="card-primary card-outline flex-grow-1 overflow-auto">
                {toolBarContent}
              </Card>
            </div>


          ) : toolBarContent
        }
        {popupPos && customPopup && (
          <div
            id="my-popup"
            className="popover bs-popover-right show"
            style={{
              position: "fixed",
              top: popupPos.top,
              left: popupPos.left,
              zIndex: 1060,
              width: 1000,
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",   // Enables scrolling inside the popup
              background: "white", // Popover bg
              borderRadius: "0.3rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}
          >
            <div className="popover-arrow" />

            <div className="popover-body">
              <CustomPopup />
            </div>
          </div>
        )}

        {showModalAdd && <ModalView
          title="Add"
          show={showModalAdd}
          onModalHide={() => setModalShowAdd(false)}
          form={create}
          submitAction={createAction}
          repository={repository}
        /*           modalSize={modalSize} */
        />}
        {showModalEdit && <ModalView
          title="Edit"
          show={showModalEdit}
          onModalHide={() => setModalShowEdit(false)}
          form={update}
          submitAction={updateAction}
          editMode={true}
          repository={repository}
        />}
        {additionalModal && <FormModal
          form={(additionalModal as ModalProps).form}
          repository={(additionalModal as ModalProps).repository}
          title={(additionalModal as ModalProps).title}
          onModalHide={(doReload) => {
            setShowAdditionalModal(false);
            if (doReload) loadData();
          }}
          show={additionalModal}
        />}
        {dataStatus === 'loading' && !dataSelector && <LoadingContent />}
      </div>
    </>
  )

  return (
    props.listView.variant == 'card' ?
      (
        <div className="h-100 m-0 p-0 d-flex flex-column mb-2 container-fluid">
          {container}
        </div>
      ) : container
  );
}
